import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import type { Schema } from 'types/schema';

export type SchemaTypeStatus = 'idle' | 'loading' | 'succeeded' | 'notFound' | 'failed';

export interface SchemaTypeEntry {
  status: SchemaTypeStatus;
  data: Schema | null;
  error: string | null;
  fetchedAt: number | null;
}

interface SchemaTypesState {
  byKey: Record<string, SchemaTypeEntry>;
}

const initialState: SchemaTypesState = { byKey: {} };

const schemaTypesSlice = createSlice({
  name: 'schemaTypes',
  initialState,
  reducers: {
    setLoading(state, action: PayloadAction<{ keys: string[] }>) {
      for (const key of action.payload.keys) {
        const status = state.byKey[key]?.status;
        // idle/failed keys are re-requestable; transition them to loading so
        // consumers see a fresh loading state instead of a stale error during
        // the retry's flush window. Never clobber loading/succeeded/notFound.
        if (!status || status === 'idle' || status === 'failed') {
          state.byKey[key] = { status: 'loading', data: null, error: null, fetchedAt: null };
        }
      }
    },
    batchFulfilled(
      state,
      action: PayloadAction<{
        schemas: Schema[];
        requestedKeys: string[];
        fetchedAt: number;
        organizationId: string;
      }>,
    ) {
      const { schemas, requestedKeys, fetchedAt, organizationId } = action.payload;
      const foundKeys = new Set<string>();
      for (const schema of schemas) {
        const key = `${organizationId}::${schema.system_name}`;
        foundKeys.add(key);
        state.byKey[key] = { status: 'succeeded', data: schema, error: null, fetchedAt };
      }
      for (const key of requestedKeys) {
        if (!foundKeys.has(key)) {
          state.byKey[key] = { status: 'notFound', data: null, error: null, fetchedAt };
        }
      }
    },
    batchFailed(state, action: PayloadAction<{ keys: string[]; error: string }>) {
      for (const key of action.payload.keys) {
        state.byKey[key] = {
          status: 'failed',
          data: null,
          error: action.payload.error,
          fetchedAt: null,
        };
      }
    },
    warmCache(
      state,
      action: PayloadAction<{ schemas: Schema[]; organizationId: string; fetchedAt: number }>,
    ) {
      const { schemas, organizationId, fetchedAt } = action.payload;
      for (const schema of schemas) {
        const key = `${organizationId}::${schema.system_name}`;
        if (state.byKey[key]?.status !== 'loading') {
          state.byKey[key] = { status: 'succeeded', data: schema, error: null, fetchedAt };
        }
      }
    },
    clearAll(state) {
      state.byKey = {};
    },
  },
});

export const { setLoading, batchFulfilled, batchFailed, warmCache, clearAll } =
  schemaTypesSlice.actions;
export default schemaTypesSlice.reducer;
